import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import {} from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { formatDateTime } from "@tellescope/utils";
import { useMemo } from "react";

export const useCallTitle = (call?: TellescopeModelFromName<"phone_calls">): string => {
  const { t } = useTranslation("timeline");

  return useMemo(() => {
    if (!call) return "";
    if (call.isVoicemail && !(call.recordingURI || call.recordingDurationInSeconds)) {
      return call.inbound ? t("Missed Call") : t("No Answer");
    }

    if (call.isVoicemail) {
      return call.inbound ? t("Voicemail") : t("Outbound Voicemail");
    }

    return call.inbound ? t("Inbound Call") : t("Outbound Call");
  }, [call, t]);
};

export const useCallRecentActivity = (call?: TellescopeModelFromName<"phone_calls">): string => {
  const { t } = useTranslation("timeline");

  const allItems: { timestamp: number; text: string }[] = useMemo(() => {
    if (!call) return [];
    const items = [
      {
        timestamp: call.createdAt.getTime(),
        text: `${t("Created:")} ${formatDateTime(call.createdAt, "MM/DD/YY at hh:mm:ss a")}`,
      },
    ];
    if (typeof call.pinnedAt === "object") {
      items.push({
        timestamp: call.pinnedAt.getTime(),
        text: `${t("Pinned:")} ${formatDateTime(call.pinnedAt, "MM/DD/YY at hh:mm:ss a")}`,
      });
    }
    if (call.answeredAt) {
      items.push({
        timestamp: call.answeredAt.getTime(),
        text: `${t("Answered:")} ${formatDateTime(call.answeredAt, "MM/DD/YYYY at hh:mm:ss a")}`,
      });
    }

    return items;
  }, [call, t]);

  const sortedItems = useMemo(() => {
    return [...allItems].sort((a, b) => (a.timestamp < b.timestamp ? 1 : a.timestamp > b.timestamp ? -1 : 0));
  }, [allItems]);

  return sortedItems[0]?.text ?? "";
};
